export const TIMED_CPT_CODES = new Set([
  // PT Treatment
  '97110', '97112', '97116', '97140', '97530', '97535', '97542',
  '97750', '97760', '97761',
  // Attended Modalities
  '97032', '97033', '97035', '97039',
]);

export const THERAPY_EVAL_CPT_CODES = new Set([
  // PT evaluation / re-evaluation
  '97161', '97162', '97163', '97164',
  // OT evaluation / re-evaluation
  '97165', '97166', '97167', '97168',
  // SLP evaluation / re-evaluation
  '92521', '92522', '92523', '92524', '92610', '92597',
]);

/**
 * Therapy discipline modifier for a provider designation (GP = PT, GO = OT,
 * GN = SLP), or null when the designation is not a therapy discipline.
 * Single source for the auto-modifier rule applied to manual, mapped and
 * derived CPT lines.
 */
export function resolveTherapyModifier(designation: string | null | undefined): 'GP' | 'GO' | 'GN' | null {
  const d = (designation || '').toUpperCase();
  if (d === 'PT' || d === 'PTA') return 'GP';
  if (d === 'OT' || d === 'OTA') return 'GO';
  if (d === 'SLP' || d === 'SLPA') return 'GN';
  return null;
}

/** Minimal CPT line shape used by the minutes / units display helpers. */
export interface CptMinutesLine {
  units?: number | string | null;
  minutes?: number | string | null;
  indirect_minutes?: number | string | null;
}

/** Units for display and charge math. Null/blank/0 means 0 units (never coerced to 1). */
export function cptUnits(cpt: CptMinutesLine | null | undefined): number {
  return Number(cpt?.units) || 0;
}

/**
 * Minutes cell text for CPT tables on visit summaries, superbills, and invoices.
 * "30" (direct only), "30 + 10 ind" (direct + indirect), "" when no minutes.
 */
export function formatCptMinutes(cpt: CptMinutesLine | null | undefined): string {
  const direct = Number(cpt?.minutes) || 0;
  const indirect = Number(cpt?.indirect_minutes) || 0;
  if (direct <= 0 && indirect <= 0) return '';
  if (indirect <= 0) return String(direct);
  return `${direct} + ${indirect} ind`;
}

/** True when any line carries direct or indirect minutes; gates the Min column. */
export function cptLinesHaveMinutes(cpts: (CptMinutesLine | null | undefined)[] | null | undefined): boolean {
  return (cpts || []).some(c => (Number(c?.minutes) || 0) > 0 || (Number(c?.indirect_minutes) || 0) > 0);
}

/**
 * CMS "always therapy" codes subject to the therapy MPPR (50% reduction to the
 * practice-expense component of the 2nd and subsequent units furnished to the
 * same patient on the same day; the highest-PE unit is paid in full).
 * Source: CMS Multiple Procedure Payment Reduction for Selected Therapy
 * Services (CR 7050 + annual updates), https://www.cms.gov/medicare/coding-billing/therapy-services
 * Verify against the current-year CMS list when importing new fee data.
 * KEEP IN SYNC with the literal in backend functions/compute_expected_reimbursement.xs.
 */
export const MPPR_THERAPY_CODES = new Set([
  // Evaluations / re-evaluations (PT, OT)
  '97161', '97162', '97163', '97164', '97165', '97166', '97167', '97168',
  // Therapeutic procedures
  '97110', '97112', '97113', '97116', '97124', '97140', '97150',
  '97530', '97533', '97535', '97537', '97542',
  '97750', '97755', '97760', '97761', '97763',
  // Modalities
  '97010', '97012', '97016', '97018', '97022', '97024', '97026', '97028',
  '97032', '97033', '97034', '97035', '97036', '97039',
  // SLP
  '92507', '92508', '92521', '92522', '92523', '92524', '92526',
  '92597', '92607', '92608', '92609', '92610', '96125',
  // HCPCS
  'G0281', 'G0283', 'G0329',
]);
